import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, GripVertical, ChevronDown, ChevronRight, ChevronUp, ArrowUp, ArrowDown } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const LOCATIONS = [
  { value: "header", label: "Header (navigare sus)" },
  { value: "footer_quick", label: "Footer — Quick Links" },
  { value: "footer_community", label: "Footer — Community" },
  { value: "footer_info", label: "Footer — Info" },
  { value: "sidebar", label: "Sidebar" },
];

const VISIBILITY = [
  { value: "all", label: "Toți" },
  { value: "guest", label: "Doar oaspeți" },
  { value: "user", label: "Doar logați" },
  { value: "vip", label: "Doar VIP" },
  { value: "admin", label: "Doar admin" },
];

const TARGETS = [
  { value: "_self", label: "Aceeași filă" },
  { value: "_blank", label: "Filă nouă" },
];

const newItem = (order = 0) => ({ id: cryptoId(), label: "", url: "", icon: "circle", target: "_self", order, visible_to: ["all"], children: [] });
function cryptoId() { return Math.random().toString(36).substring(2, 12); }

export default function IDGMenusTab() {
  const [menus, setMenus] = useState([]);
  const [activeLoc, setActiveLoc] = useState("header");
  const [draft, setDraft] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/idg/menus");
      setMenus(data);
      const current = data.find((m) => m.location === activeLoc);
      if (current) {
        // Sort draft items initially by their current order
        const sortedItems = [...(current.items || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
        setDraft({ ...structuredClone(current), items: sortedItems });
      } else {
        setDraft({ location: activeLoc, name: LOCATIONS.find((l) => l.value === activeLoc)?.label || activeLoc, items: [] });
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeLoc]);

  const save = async () => {
    try {
      // Strictly re-index order integers for each item before saving
      const itemsWithOrder = (draft.items || []).map((item, idx) => ({
        ...item,
        order: idx
      }));
      const payload = {
        ...draft,
        items: itemsWithOrder
      };

      if (payload.id) {
        await api.patch(`/idg/menus/${payload.id}`, payload);
      } else {
        await api.post("/idg/menus", payload);
      }
      toast.success("Meniu salvat cu succes!");
      window.dispatchEvent(new CustomEvent("idg:menu-updated"));
      if (window.BroadcastChannel) {
        new BroadcastChannel("idg_sync").postMessage("menu-updated");
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!draft) return <div className="text-zinc-500 text-sm tf-mono">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="idg-menus-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {LOCATIONS.map((l) => (
          <button
            key={l.value}
            onClick={() => setActiveLoc(l.value)}
            className={`px-3 py-1.5 text-xs tf-clip-card-sm transition-colors ${activeLoc === l.value ? "bg-[var(--tf-primary)] text-black font-bold" : "tf-surface text-zinc-300 hover:bg-white/5"}`}
            data-testid={`menu-location-${l.value}`}
          >
            {l.label}
          </button>
        ))}
      </div>

      <div className="tf-surface tf-clip-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="tf-section-title">{draft.name || LOCATIONS.find((l) => l.value === activeLoc)?.label}</h3>
            <p className="text-[11px] text-zinc-500 font-mono mt-0.5">Trage de iconița din stânga sau folosește săgețile pentru a reordona elementele.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setDraft({ ...draft, items: [...(draft.items || []), newItem(draft.items?.length || 0)] })} className="tf-btn-secondary tf-clip-btn text-xs flex items-center gap-1" data-testid="menu-add-item">
              <Plus className="w-3 h-3" /> Item nou
            </button>
            <button onClick={save} className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1 shadow-lg" data-testid="menu-save">
              <Save className="w-3 h-3" /> Salvează meniu
            </button>
          </div>
        </div>
        <MenuItemList items={draft.items || []} onChange={(items) => setDraft({ ...draft, items })} />
      </div>
    </div>
  );
}

function MenuItemList({ items, onChange }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((x) => x.id === active.id);
    const newIdx = items.findIndex((x) => x.id === over.id);
    if (oldIdx !== -1 && newIdx !== -1) {
      const moved = arrayMove(items, oldIdx, newIdx);
      const reordered = moved.map((item, index) => ({
        ...item,
        order: index
      }));
      onChange(reordered);
    }
  };

  const moveUp = (idx) => {
    if (idx <= 0) return;
    const moved = arrayMove(items, idx, idx - 1);
    const reordered = moved.map((item, index) => ({ ...item, order: index }));
    onChange(reordered);
  };

  const moveDown = (idx) => {
    if (idx >= items.length - 1) return;
    const moved = arrayMove(items, idx, idx + 1);
    const reordered = moved.map((item, index) => ({ ...item, order: index }));
    onChange(reordered);
  };

  const updateItem = (idx, key, value) => {
    const next = [...items]; 
    next[idx] = { ...next[idx], [key]: value }; 
    onChange(next);
  };
  const removeItem = (idx) => { 
    const next = [...items]; 
    next.splice(idx, 1); 
    onChange(next.map((item, index) => ({ ...item, order: index }))); 
  };
  const addChild = (idx) => { const next = [...items]; next[idx] = { ...next[idx], children: [...(next[idx].children || []), newItem()] }; onChange(next); };
  const updateChild = (idx, cIdx, key, value) => {
    const next = [...items];
    const children = [...(next[idx].children || [])];
    children[cIdx] = { ...children[cIdx], [key]: value };
    next[idx] = { ...next[idx], children };
    onChange(next);
  };
  const removeChild = (idx, cIdx) => {
    const next = [...items];
    const children = [...(next[idx].children || [])];
    children.splice(cIdx, 1);
    next[idx] = { ...next[idx], children };
    onChange(next);
  };

  if (!items?.length) return <p className="text-xs tf-mono text-zinc-500 py-4 text-center">Nicio intrare. Adaugă una.</p>;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((x) => x.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((item, idx) => (
            <MenuItemRow
              key={item.id}
              item={item}
              idx={idx}
              totalItems={items.length}
              onMoveUp={() => moveUp(idx)}
              onMoveDown={() => moveDown(idx)}
              onUpdate={(k, v) => updateItem(idx, k, v)}
              onRemove={() => removeItem(idx)}
              onAddChild={() => addChild(idx)}
              onUpdateChild={(cIdx, k, v) => updateChild(idx, cIdx, k, v)}
              onRemoveChild={(cIdx) => removeChild(idx, cIdx)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function MenuItemRow({ item, idx, totalItems, onMoveUp, onMoveDown, onUpdate, onRemove, onAddChild, onUpdateChild, onRemoveChild }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const [expanded, setExpanded] = useState(false);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="tf-surface-elev tf-clip-card-sm border border-white/5 bg-[#0f1118]" data-testid={`menu-item-${idx}`}>
      <div className="grid grid-cols-12 gap-2 items-center p-2">
        
        {/* Drag handle & Order number & Quick Up/Down buttons */}
        <div className="col-span-2 flex items-center gap-1.5">
          <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-[var(--tf-primary)] p-1 flex items-center justify-center">
            <GripVertical className="w-4 h-4" />
          </button>
          <div className="flex flex-col">
            <button 
              type="button" 
              onClick={onMoveUp} 
              disabled={idx === 0} 
              className="text-zinc-500 hover:text-white disabled:opacity-20 disabled:hover:text-zinc-500 leading-none p-0.5"
              title="Mută mai sus"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button 
              type="button" 
              onClick={onMoveDown} 
              disabled={idx === totalItems - 1} 
              className="text-zinc-500 hover:text-white disabled:opacity-20 disabled:hover:text-zinc-500 leading-none p-0.5"
              title="Mută mai jos"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>
          <span className="font-mono text-[10px] text-zinc-500 font-bold px-1.5 py-0.5 bg-black/40 rounded">
            #{idx + 1}
          </span>
        </div>

        <input className="col-span-2 tf-input py-1 text-xs" placeholder="Etichetă" value={item.label || ""} onChange={(e) => onUpdate("label", e.target.value)} data-testid={`menu-item-label-${idx}`} />
        <input className="col-span-3 tf-input py-1 text-xs" placeholder="URL" value={item.url || ""} onChange={(e) => onUpdate("url", e.target.value)} data-testid={`menu-item-url-${idx}`} />
        <input className="col-span-1 tf-input py-1 text-xs font-mono" placeholder="icon" value={item.icon || ""} onChange={(e) => onUpdate("icon", e.target.value)} title="Lucide icon key" />
        
        <select className="col-span-1 tf-input py-1 text-xs" value={item.target || "_self"} onChange={(e) => onUpdate("target", e.target.value)}>
          {TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        
        <select className="col-span-2 tf-input py-1 text-xs" value={item.visible_to?.[0] || "all"} onChange={(e) => onUpdate("visible_to", [e.target.value])}>
          {VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>

        <div className="col-span-1 flex justify-end gap-1">
          <button onClick={() => setExpanded((v) => !v)} className="text-zinc-400 hover:text-white p-1" title="Subitems">
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          <button onClick={onRemove} className="text-red-500 hover:bg-red-500/10 p-1" data-testid={`menu-item-remove-${idx}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="pl-8 pr-2 pb-2 space-y-1.5 bg-black/30">
          {(item.children || []).map((child, cIdx) => (
            <div key={child.id || cIdx} className="grid grid-cols-12 gap-2 items-center" data-testid={`menu-subitem-${idx}-${cIdx}`}>
              <span className="col-span-1 text-zinc-500 text-center">↳</span>
              <input className="col-span-3 tf-input py-1 text-xs" placeholder="Etichetă" value={child.label || ""} onChange={(e) => onUpdateChild(cIdx, "label", e.target.value)} />
              <input className="col-span-4 tf-input py-1 text-xs" placeholder="URL" value={child.url || ""} onChange={(e) => onUpdateChild(cIdx, "url", e.target.value)} />
              <select className="col-span-3 tf-input py-1 text-xs" value={child.visible_to?.[0] || "all"} onChange={(e) => onUpdateChild(cIdx, "visible_to", [e.target.value])}>
                {VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
              <button onClick={() => onRemoveChild(cIdx)} className="col-span-1 text-red-500 hover:bg-red-500/10 p-1 justify-self-end">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button onClick={onAddChild} className="text-[10px] text-[var(--tf-primary)] hover:underline flex items-center gap-1" data-testid={`menu-add-subitem-${idx}`}>
            <Plus className="w-3 h-3" /> Sub-item
          </button>
        </div>
      )}
    </div>
  );
}
